/**
 * Legal Content Hook
 * Auto-switches legal documents by country/language
 * No fallback to wrong jurisdiction allowed
 */
import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '@/lib/context';

// Language to country jurisdiction mapping
const LANGUAGE_JURISDICTION_MAP = {
  et: 'ee',
  en: 'en',
  nb: 'no',
  fi: 'fi',
  sv: 'se',
  ru: 'ru'
};

// Document types
const DOCUMENT_TYPES = {
  TERMS: 'terms',
  PRIVACY: 'privacy',
  COOKIES: 'cookies'
};

// Dynamic imports for legal documents
const loadLegalDocument = async (jurisdiction, documentType) => {
  try {
    const module = await import(`@/legal/${jurisdiction}/${documentType}.json`);
    return module.default || module;
  } catch (error) {
    console.error(`[Legal] Failed to load ${documentType} for ${jurisdiction}:`, error);
    // NO fallback to wrong jurisdiction - return null
    return null;
  }
};

export function useLegalContent(documentType = DOCUMENT_TYPES.TERMS) {
  const { language } = useLanguage();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const jurisdiction = useMemo(() => {
    return LANGUAGE_JURISDICTION_MAP[language] || 'en';
  }, [language]);

  useEffect(() => {
    let cancelled = false;
    
    const loadDocument = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const doc = await loadLegalDocument(jurisdiction, documentType);
        
        if (cancelled) return;
        
        if (!doc) {
          setError(`Legal document not available for ${jurisdiction}`);
          setDocument(null);
        } else {
          setDocument(doc);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setDocument(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
    };
  }, [jurisdiction, documentType]);

  return {
    document,
    loading,
    error,
    jurisdiction,
    documentType,
    version: document?.version,
    effectiveDate: document?.effectiveDate,
    sections: document?.sections || {},
    companyName: document?.companyName,
    email: document?.email
  };
}

// Pre-built hooks for specific documents
export function useTermsOfService() {
  return useLegalContent(DOCUMENT_TYPES.TERMS);
}

export function usePrivacyPolicy() {
  return useLegalContent(DOCUMENT_TYPES.PRIVACY);
}

export function useCookiePolicy() {
  return useLegalContent(DOCUMENT_TYPES.COOKIES);
}

// Export types for components
export { DOCUMENT_TYPES, LANGUAGE_JURISDICTION_MAP };

export default useLegalContent;
